import { useCallback, useRef, useState } from "react";
import { CellCoord } from "./types";

interface UseGridNavigationParams {
  rowCount: number;
  colCount: number;
  // Whether a column is text-like: typing enters it directly; other types edit only via Enter/F2.
  isEditableCol?: (col: number) => boolean;
  // Ctrl/Cmd+S saves just the active learner's row.
  onSaveRow?: (row: number) => void;
}

export interface GridNavigation {
  activeCell: CellCoord | null;
  isActive: (row: number, col: number) => boolean;
  // Select a cell from a focus event; focusTarget decides mode (inner control → edit, container → select).
  activate: (row: number, col: number, focusTarget?: EventTarget | null) => void;
  // ref callback so the hook can focus a cell's container / editor when navigating by keyboard.
  registerCell: (row: number, col: number) => (node: HTMLElement | null) => void;
  handleKeyDown: (event: React.KeyboardEvent) => void;
}

const clamp = (value: number, max: number) => Math.max(0, Math.min(value, max));

// Save shortcut is ⌘+S on macOS and Ctrl+S on Windows/Linux (matches the shortcuts drawer).
const isMacPlatform = (): boolean => typeof navigator !== "undefined" && /mac/i.test(navigator.platform || navigator.userAgent || "");

const INNER_FOCUSABLE = "input, textarea, select, button, [tabindex]";

// Focus the container → select mode (tabIndex=-1, so keydown bubbles to the grid handler).
const focusContainer = (node: HTMLElement | null) => node?.focus();

// Focus the editor inside the cell → edit mode (caret / active control).
const focusInner = (node: HTMLElement | null) => {
  if (!node) return;
  const focusable = node.querySelector<HTMLElement>(INNER_FOCUSABLE);
  (focusable || node).focus();
};

// Spreadsheet-style keyboard nav, two modes: Select (arrows move; Enter/F2/typing enters) and
// Edit (Enter commits + moves down; Esc returns to select; other keys are native).
export const useGridNavigation = ({ rowCount, colCount, isEditableCol, onSaveRow }: UseGridNavigationParams): GridNavigation => {
  const [activeCell, setActiveCell] = useState<CellCoord | null>(null);
  const [editing, setEditing] = useState(false);
  const nodes = useRef<Map<string, HTMLElement | null>>(new Map());

  const key = (row: number, col: number) => `${row}-${col}`;

  const registerCell = useCallback(
    (row: number, col: number) => (node: HTMLElement | null) => {
      nodes.current.set(key(row, col), node);
    },
    []
  );

  // Select a cell (container focused, no caret) — used by keyboard navigation.
  const moveTo = useCallback((row: number, col: number) => {
    setActiveCell({ row, col });
    setEditing(false);
    focusContainer(nodes.current.get(`${row}-${col}`) || null);
  }, []);

  // Drop into the cell's editor.
  const enterEdit = useCallback((row: number, col: number) => {
    setEditing(true);
    focusInner(nodes.current.get(`${row}-${col}`) || null);
  }, []);

  const activate = useCallback((row: number, col: number, focusTarget?: EventTarget | null) => {
    setActiveCell(prev => (prev && prev.row === row && prev.col === col ? prev : { row, col }));
    // Focus landed on an inner control (click into an input) → edit; on the cell container itself → select.
    const container = nodes.current.get(key(row, col)) || null;
    setEditing(!!focusTarget && focusTarget !== container);
  }, []);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      const current = activeCell;
      if (!current) return;
      const { row, col } = current;
      const textCell = isEditableCol ? isEditableCol(col) : false;

      // Save the active row — ⌘+S on Mac, Ctrl+S on Windows/Linux (overrides the browser's Save).
      const wantsSave = (event.key === "s" || event.key === "S") && (isMacPlatform() ? event.metaKey : event.ctrlKey);
      if (wantsSave) {
        event.preventDefault();
        onSaveRow?.(row);
        return;
      }

      // Edit mode: Enter commits and moves down; Esc returns to select; the rest is native.
      if (editing) {
        if (event.key === "Escape") {
          event.preventDefault();
          moveTo(row, col);
          return;
        }
        if (event.key === "Enter" && !event.shiftKey) {
          event.preventDefault();
          moveTo(clamp(row + 1, rowCount - 1), col);
          return;
        }
        return;
      }

      // Select mode.
      switch (event.key) {
        case "ArrowUp":
          event.preventDefault();
          moveTo(clamp(row - 1, rowCount - 1), col);
          break;
        case "ArrowDown":
          event.preventDefault();
          moveTo(clamp(row + 1, rowCount - 1), col);
          break;
        case "ArrowLeft":
          event.preventDefault();
          moveTo(row, clamp(col - 1, colCount - 1));
          break;
        case "ArrowRight":
          event.preventDefault();
          moveTo(row, clamp(col + 1, colCount - 1));
          break;
        case "Enter":
          if (event.shiftKey) return;
          // Return goes INTO the cell; a second Return (now edit mode) moves to the row below.
          event.preventDefault();
          enterEdit(row, col);
          break;
        case "F2":
          event.preventDefault();
          enterEdit(row, col);
          break;
        default:
          // Typing a printable character on a text cell drops into its editor.
          if (textCell && event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
            enterEdit(row, col);
          }
          break;
      }
    },
    [activeCell, rowCount, colCount, moveTo, enterEdit, editing, isEditableCol, onSaveRow]
  );

  const isActive = useCallback((row: number, col: number) => !!activeCell && activeCell.row === row && activeCell.col === col, [activeCell]);

  return { activeCell, isActive, activate, registerCell, handleKeyDown };
};
